import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getAuthHeaders } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO = 'https://customer-assets.emergentagent.com/job_womens-wellness-72/artifacts/dyehgy3v_Logo.jpg';
const BG_IMAGE = 'https://customer-assets.emergentagent.com/job_womens-wellness-72/artifacts/66wkwhvo_PHOTO-2026-03-29-20-09-08%20%282%29.jpg';

// ── Shared input style ────────────────────────────────────────────────────────
const INPUT = 'bg-[#FFFAF0] border-2 border-[#E7E5E4] rounded-2xl pl-11 pr-5 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] transition-all outline-none w-full text-[#292524] placeholder:text-[#A8A29E] font-body text-sm';
const INPUT_EYE = 'bg-[#FFFAF0] border-2 border-[#E7E5E4] rounded-2xl pl-11 pr-12 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] transition-all outline-none w-full text-[#292524] placeholder:text-[#A8A29E] font-body text-sm';

function PasswordInput({ id, testId, value, onChange, placeholder, show, onToggle }) {
  return (
    <div className="relative">
      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" strokeWidth={1.5} />
      <input
        id={id}
        data-testid={testId}
        type={show ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={INPUT_EYE}
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
      />
      <button type="button" onClick={onToggle}
        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A29E] hover:text-[#F59E0B] transition-colors">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function MsgBox({ text, type }) {
  if (!text) return null;
  const isErr = type === 'error';
  return (
    <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 mb-4 text-sm
      ${isErr ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-green-50 border border-green-200 text-green-700'}`}>
      {isErr ? <AlertCircle className="w-4 h-4 shrink-0" /> : <CheckCircle className="w-4 h-4 shrink-0" />}
      <span>{text}</span>
    </div>
  );
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  // view: 'login' | 'change-password' | 'force-change'
  const [view, setView] = useState('login');

  // ── Login form state ──────────────────────────────────────────────────────
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // ── Change-password form state ────────────────────────────────────────────
  const [cpPhone, setCpPhone] = useState('');
  const [cpOld, setCpOld] = useState('');
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [showCpOld, setShowCpOld] = useState(false);
  const [showCpNew, setShowCpNew] = useState(false);
  const [showCpConfirm, setShowCpConfirm] = useState(false);
  const [cpLoading, setCpLoading] = useState(false);
  const [cpError, setCpError] = useState('');
  const [cpSuccess, setCpSuccess] = useState('');

  // ── Force-change form state (first login) ─────────────────────────────────
  const [fcNew, setFcNew] = useState('');
  const [fcConfirm, setFcConfirm] = useState('');
  const [showFcNew, setShowFcNew] = useState(false);
  const [showFcConfirm, setShowFcConfirm] = useState(false);
  const [fcLoading, setFcLoading] = useState(false);
  const [fcError, setFcError] = useState('');
  const [fcSuccess, setFcSuccess] = useState('');
  const [fcPendingRole, setFcPendingRole] = useState('');

  if (user && view === 'login') {
    navigate(user.role === 'admin' ? '/admin' : '/student');
    return null;
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!phone.trim() || !password) {
      setLoginError('Please enter your phone number and password.'); return;
    }
    setLoginLoading(true);
    setLoginError('');
    try {
      const data = await login(phone.trim(), password);
      if (data.must_change_password) {
        // Student still has default password — force them to change it
        setFcPendingRole(data.role);
        setView('force-change');
      } else {
        navigate(data.role === 'admin' ? '/admin' : '/student');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLoginError(typeof detail === 'string' ? detail : 'Invalid phone number or password. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!cpPhone.trim() || !cpOld || !cpNew || !cpConfirm) {
      setCpError('Please fill in all fields.'); return;
    }
    if (cpNew !== cpConfirm) {
      setCpError('New passwords do not match.'); return;
    }
    setCpLoading(true);
    setCpError('');
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        phone: cpPhone.trim(),
        old_password: cpOld,
        new_password: cpNew,
      });
      setCpSuccess('Password updated successfully. Please login using your new password.');
      setTimeout(() => {
        setPhone(cpPhone.trim());
        setPassword('');
        setView('login');
        setCpSuccess('');
        setCpPhone(''); setCpOld(''); setCpNew(''); setCpConfirm('');
      }, 2000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setCpError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setCpLoading(false);
    }
  };

  const handleForceChange = async (e) => {
    e.preventDefault();
    if (!fcNew || !fcConfirm) {
      setFcError('Please fill in both fields.'); return;
    }
    if (fcNew !== fcConfirm) {
      setFcError('Passwords do not match.'); return;
    }
    setFcLoading(true);
    setFcError('');
    try {
      const token = localStorage.getItem('mff_token');
      await axios.post(`${API}/api/student/change-password`,
        { new_password: fcNew, confirm_password: fcConfirm },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFcSuccess('Password updated successfully.');
      setTimeout(() => {
        navigate(fcPendingRole === 'admin' ? '/admin' : '/student');
      }, 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setFcError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setFcLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className="min-h-screen flex items-center justify-center relative px-4 py-10"
      style={{ backgroundImage: `url(${BG_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      <div className="relative z-10 w-full max-w-sm animate-fade-in-up">

        {/* ── CARD ─────────────────────────────────────────────────── */}
        <div className="bg-white rounded-3xl px-7 py-8 shadow-2xl border border-white/40">

          {/* Logo */}
          <div className="flex justify-center -mt-2 mb-4">
            <img src={LOGO} alt="Meena's Fitness Forever" className="w-36 sm:w-40 object-contain"
              style={{ mixBlendMode: 'multiply', display: 'block' }} />
          </div>

          {/* ── VIEW: LOGIN ──────────────────────────────────────────── */}
          {view === 'login' && (
            <>
              <div className="text-center mb-6">
                <h1 className="font-heading text-2xl font-semibold text-[#292524]">Welcome back 👋</h1>
                <p className="text-[#78716C] text-sm mt-1 font-body">Let's get stronger together</p>
              </div>

              <MsgBox text={loginError} type="error" />

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-1.5" htmlFor="login-phone">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" strokeWidth={1.5} />
                    <input
                      id="login-phone"
                      data-testid="login-phone-input"
                      type="tel" inputMode="numeric"
                      placeholder="Enter your phone number"
                      value={phone} onChange={e => setPhone(e.target.value)}
                      className={INPUT}
                      autoComplete="tel" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-1.5" htmlFor="login-password">Password</label>
                  <PasswordInput
                    id="login-password" testId="login-password-input"
                    value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    show={showPass} onToggle={() => setShowPass(v => !v)}
                  />
                  {/* Change Password link */}
                  <p className="text-xs font-body mt-2 text-right">
                    <button type="button"
                      data-testid="change-password-link"
                      onClick={() => { setView('change-password'); setCpError(''); setCpSuccess(''); }}
                      className="text-blue-500 hover:text-blue-700 font-medium transition-colors">
                      Change Password
                    </button>
                  </p>
                </div>

                <button
                  type="submit"
                  data-testid="login-submit-button"
                  disabled={loginLoading}
                  className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3.5 font-heading font-medium text-base transition-all duration-300 shadow-button hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0 mt-1"
                >
                  {loginLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Signing in...
                    </span>
                  ) : 'Login'}
                </button>
              </form>

              <div className="text-center mt-5">
                <button
                  data-testid="back-to-home-button"
                  onClick={() => navigate('/')}
                  className="text-[#57534E] hover:text-[#292524] text-sm font-body transition-colors"
                >
                  ← Back to Home
                </button>
              </div>
            </>
          )}

          {/* ── VIEW: CHANGE PASSWORD ────────────────────────────────── */}
          {view === 'change-password' && (
            <>
              <div className="text-center mb-6">
                <h1 className="font-heading text-2xl font-semibold text-[#292524]">Change Password</h1>
                <p className="text-[#78716C] text-sm mt-1 font-body">Update your account password</p>
              </div>

              <MsgBox text={cpError} type="error" />
              <MsgBox text={cpSuccess} type="success" />

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-1.5">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" strokeWidth={1.5} />
                    <input
                      data-testid="cp-phone-input"
                      type="tel" inputMode="numeric"
                      placeholder="Your registered phone number"
                      value={cpPhone} onChange={e => setCpPhone(e.target.value)}
                      className={INPUT}
                      autoCorrect="off" autoCapitalize="off" spellCheck={false}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-1.5">Old Password</label>
                  <PasswordInput
                    testId="cp-old-input"
                    value={cpOld} onChange={e => setCpOld(e.target.value)}
                    placeholder="Your current password"
                    show={showCpOld} onToggle={() => setShowCpOld(v => !v)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-1.5">New Password</label>
                  <PasswordInput
                    testId="cp-new-input"
                    value={cpNew} onChange={e => setCpNew(e.target.value)}
                    placeholder="Enter new password"
                    show={showCpNew} onToggle={() => setShowCpNew(v => !v)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-1.5">Confirm New Password</label>
                  <PasswordInput
                    testId="cp-confirm-input"
                    value={cpConfirm} onChange={e => setCpConfirm(e.target.value)}
                    placeholder="Re-enter new password"
                    show={showCpConfirm} onToggle={() => setShowCpConfirm(v => !v)}
                  />
                </div>

                <button
                  type="submit"
                  data-testid="cp-submit-button"
                  disabled={cpLoading}
                  className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3.5 font-heading font-medium text-base transition-all shadow-button hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed mt-1"
                >
                  {cpLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Updating...
                    </span>
                  ) : 'Update Password'}
                </button>
              </form>

              <p className="text-center mt-4">
                <button type="button"
                  data-testid="back-to-login-link"
                  onClick={() => { setView('login'); setCpError(''); setCpSuccess(''); }}
                  className="text-blue-500 hover:text-blue-700 text-sm font-medium transition-colors font-body">
                  Back to Login
                </button>
              </p>

              <p className="text-center text-xs text-[#A8A29E] font-body mt-4">
                If you don't remember your old password, please contact the admin.
              </p>
            </>
          )}

          {/* ── VIEW: FORCE CHANGE (first login) ─────────────────────── */}
          {view === 'force-change' && (
            <>
              <div className="text-center mb-6">
                <h1 className="font-heading text-2xl font-semibold text-[#292524]">Create Your Password</h1>
                <p className="text-[#78716C] text-sm mt-1 font-body">Please set a personal password to continue</p>
              </div>

              <MsgBox text={fcError} type="error" />
              <MsgBox text={fcSuccess} type="success" />

              <form onSubmit={handleForceChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-1.5">New Password</label>
                  <PasswordInput
                    testId="fc-new-input"
                    value={fcNew} onChange={e => setFcNew(e.target.value)}
                    placeholder="Create a new password"
                    show={showFcNew} onToggle={() => setShowFcNew(v => !v)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#57534E] mb-1.5">Confirm New Password</label>
                  <PasswordInput
                    testId="fc-confirm-input"
                    value={fcConfirm} onChange={e => setFcConfirm(e.target.value)}
                    placeholder="Re-enter new password"
                    show={showFcConfirm} onToggle={() => setShowFcConfirm(v => !v)}
                  />
                </div>

                <button
                  type="submit"
                  data-testid="fc-submit-button"
                  disabled={fcLoading}
                  className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3.5 font-heading font-medium text-base transition-all shadow-button hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed mt-1"
                >
                  {fcLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Updating...
                    </span>
                  ) : 'Update Password'}
                </button>
              </form>
            </>
          )}

        </div>

        <p className="text-center text-xs text-white/50 mt-5 font-body">
          Meena's Fitness Forever &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
